import { useEffect, useRef, useState } from 'react';
import { storage } from '../../lib/firebase';
import { uploadFile, type UploadHandle } from '../../lib/upload';
import { MAX_UPLOAD_BYTES, UPLOAD_ACCEPT } from '../../lib/data-model';
import { ProgressBar, toast } from '../ui';
import { errOf } from './ListAdmin';

export type UploadValue = { path?: string; url?: string; name?: string };

/** Dropzone + progress + cancel. On success sets {path,url,name}; failures toast. */
export function UploadField({
  courseId,
  value,
  onChange,
  label = 'Choose or drop a file',
  accept = UPLOAD_ACCEPT
}: {
  courseId: string;
  value: UploadValue;
  onChange: (v: UploadValue) => void;
  label?: string;
  accept?: string;
}) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(1);
  const [name, setName] = useState(value.name ?? '');
  const handle = useRef<UploadHandle | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value.path && !busy) setName('');
  }, [value.path, busy]);

  const pick = (file: File) => {
    const ext = `.${(file.name.split('.').pop() ?? '').toLowerCase()}`;
    const allowed = accept.split(',').map(s => s.trim().toLowerCase());
    if (!allowed.includes(ext)) {
      toast(`Unsupported file type (${ext || 'unknown'})`, 'err');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast(`File too large — max ${Math.round(MAX_UPLOAD_BYTES / 1048576)} MB`, 'err');
      return;
    }
    setName(file.name);
    setLoaded(0);
    setTotal(file.size);
    setBusy(true);
    const h = uploadFile(storage, file, courseId, (l, t) => {
      setLoaded(l);
      setTotal(t || 1);
    });
    handle.current = h;
    h.promise
      .then(r => {
        setBusy(false);
        onChange({ path: r.path, url: r.url, name: r.name });
        toast('Uploaded', 'ok');
      })
      .catch(e => {
        setBusy(false);
        const code = (e as { code?: string }).code;
        if (code !== 'cancel') toast(`Upload failed: ${errOf(e)}`, 'err');
        else toast('Upload cancelled', 'err');
      });
  };

  const onFiles = (files: FileList | null) => {
    const f = files && files[0];
    if (f) pick(f);
  };

  if (busy) {
    return (
      <div className="upload-row">
        <span className="name">{name}</span>
        <div style={{ flex: '1 1 140px', minWidth: 100 }}>
          <ProgressBar pct={total ? (loaded / total) * 100 : 0} label="Uploading" />
        </div>
        <span className="mono" style={{ fontSize: 12, color: 'var(--ink3)' }}>
          {Math.round((total ? loaded / total : 0) * 100)}%
        </span>
        <button className="row-btn danger" onClick={() => handle.current?.cancel()}>
          Cancel
        </button>
      </div>
    );
  }

  if (value.path) {
    return (
      <div className="upload-row">
        <span aria-hidden>📄</span>
        <span className="name" title={value.path}>
          {value.name || 'Uploaded file'}
        </span>
        <button
          className="btn btn-outline btn-sm"
          onClick={() => {
            onChange({});
            setName('');
            if (input.current) input.current.value = '';
          }}
        >
          Remove
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => input.current?.click()}>
          Replace
        </button>
        <input ref={input} type="file" accept={accept} hidden onChange={e => onFiles(e.target.files)} />
      </div>
    );
  }

  return (
    <div
      className={`dropzone${drag ? ' drag' : ''}`}
      onClick={() => input.current?.click()}
      onDragOver={e => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => {
        e.preventDefault();
        setDrag(false);
        onFiles(e.dataTransfer.files);
      }}
    >
      <input ref={input} type="file" accept={accept} hidden onChange={e => onFiles(e.target.files)} />
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '.1em' }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 13, marginTop: 6 }}>
        Max {Math.round(MAX_UPLOAD_BYTES / 1048576)} MB · {accept}
      </div>
    </div>
  );
}
